import { Injectable, Injector, OnInit } from "@angular/core";
import { ActivatedRoute, Data, NavigationCancel, NavigationEnd, Router } from "@angular/router";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { AuthService } from "@core/auth.service";
import { UtilityService } from "@core/utility.service";
import { deleteApp } from 'firebase/app';
import { terminate } from 'firebase/firestore';
import { catchError, filter, of, Subject, take, takeUntil } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { AppVersionService } from "../appVersions/app-version.service";
import { ClevertapService } from "../clevertap.service";
import { FirebaseFactoryService } from '../firebase-factory.service';
import { GTMSegmentService } from "../gtm-segment.service";
import { MixpanelService } from "../mixpanel.service";
import { PosthogService } from "../posthog.service";
import { SwForceUpdateService } from '../sw-force-update.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: "root",
})
export class AppInitializerService {
  private loadEventListener: any;
  private networkListener: any;
  private unsubscribe$ = new Subject<void>();
  constructor(
    private authService: AuthService,
    private cleverTapService: ClevertapService,
    private injector: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private mixPanelService: MixpanelService,
    private posthogService: PosthogService,
    private GTMSagment: GTMSegmentService,
    private appVersion: AppVersionService,
    private firebaseFactoryService: FirebaseFactoryService,
    private swForceUpdateService: SwForceUpdateService,
    private firestore: AngularFirestore,
  ) { 
    this.initialize();
  }

  checkUpdate() {
    this.appVersion.checkForUpdates();
  }

  watchOfflineMode() {
    try {
      this.cleverTapService.init();
      Network.getStatus()
        .then((status) => {
          this.cleverTapService.setOffline(!status?.connected);
        })
        .catch(() => {
          return;
        });

      this.loadEventListener = () => {
        // now we listen for network status changes
        this.networkListener = Network.addListener("networkStatusChange", (e) => {
          const authService = this.injector.get(AuthService);
          this.cleverTapService.setOffline(!e?.connected);
          if (e?.connected) {
            if (authService?.getConstantsMsg) {
              authService
                ?.getConstantsMsg("1000")
                ?.then((translatedMsg) => {
                  console.info(translatedMsg);
                  const defaultMsg = {
                    message: {
                      line_1: "Back online!",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle info.svg",
                      type: "Info",
                    },
                  };
                  authService?.showToastMessage(translatedMsg, defaultMsg);
                })
                ?.catch((error) => {
                  console.error("Error showing online message:", error);
                });
            }
          } else {
            const language = authService?.selectedlanguage$?.value;
            const translatedMsg = {
              message: {
                line_1: CommonVariable?.offlineToastMessage?.[language],
                line_2: "",
              },
              style: {
                icon: "tick circle info.svg",
                type: "Info",
              },
            };
            const defaultMsg = {
              message: {
                line_1: `You're currently offline. Some features may be unavailable`,
                line_2: "",
              },
              style: {
                icon: "tick circle info.svg",
                type: "Info",
              },
            };
            authService?.showToastMessage(translatedMsg, defaultMsg);
          }
        });
      };

      // Add the load event listener
      if (document.readyState === "complete") {
        this.loadEventListener();
      } else {
        window.addEventListener("load", this.loadEventListener);
      }
    } catch (error) {
      return error;
    }
  }

  // Add cleanup method
  cleanupNetworkListeners() {
    try {
      // Remove the load event listener if it exists
      if (this.loadEventListener) {
        window.removeEventListener("load", this.loadEventListener);
        this.loadEventListener = null;
      }

      // Remove the network status listener if it exists
      if (this.networkListener) {
        this.networkListener.remove();
        this.networkListener = null;
      }
    } catch (error) {
      console.error("Error cleaning up network listeners:", error);
    }
  }

  handleRouteEvents() {
        try {
          this.router.events
            ?.pipe(
              takeUntil(this.unsubscribe$),
              catchError((error) => {
                return error;
              })
            )
            .subscribe((event) => {
              if (event instanceof NavigationEnd) {
              var uri = event?.url;
              if (UtilityService.isAppInstalledOnHomePage() && !event?.url.includes("standalone")) {
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: { mode: "standalone" },
                  queryParamsHandling: "merge",
                  replaceUrl: true,
                });
              }
              // Track page views immediately on route changes; do not block on UID resolution.
              this.cleverTapService.checkRoutingModule(uri);
              const category = uri?.split("/").pop();
              this.GTMSagment.GTM_CJ_site_pages(category?.split("?").shift());

              this.authService.getUid$("AppInitializerService")?.pipe(take(1))?.subscribe((userId) => {
                if (event?.url && event?.url?.indexOf("inbox=") >= 0) {
                  this.route.queryParams
                    .pipe(filter((params) => params?.inbox))
                    ?.pipe(
                      takeUntil(this.unsubscribe$),
                      catchError((error) => {
                        console.error(error);
                        return of();
                      })
                    )
                    .subscribe((inbox) => {
                      let data = {
                        inboxId: inbox?.inbox,
                        uid: userId,
                      };
                      this.postInbox(data)
                        ?.pipe(
                          takeUntil(this.unsubscribe$),
                          catchError((error) => {
                            return error;
                          })
                        )
                        .subscribe();
                      this.router.navigate([], {
                        relativeTo: this.route,
                        queryParamsHandling: "",
                        replaceUrl: true,
                      });
                    });
                }
                if (userId) {
                  this.mixPanelService.init(userId);
                  this.mixPanelService.getViewMixpanel(uri?.split("/").pop());
                  // HATDEV-100772: PostHog identify with same internal UID (no alias/reset)
                  this.posthogService.identify(userId);
                }
              });
              }

              if (event instanceof NavigationCancel) {
              }
            });
        } catch (e) {
          console.log(e);
        }
  }

  private postInbox(data: Data) {
    return this.authService.getV2CloudFunction("urlinboxinsertionv2", data);
  }

  async getUDID() {
    if (!Device) {
      return;
    }
    try {
      const info = await Device.getId();
      if (!info || !info?.identifier) return;
      CommonVariable.UDId = info?.identifier;
    } catch (error) {
      return error;
    }
  }

  errorHandler() {
    window.onerror = (errorMsg, url, lineNumber) => {
      const msg = typeof errorMsg === 'string' ? errorMsg.toLowerCase() : '';

      if (
        msg.includes("firebaseerror") &&
        msg.includes("a newer version of the firestore sdk was previously used")
      ) {
        console.error("Firestore SDK compatibility error:", errorMsg, "at", url, "line:", lineNumber);
        this.clearIndexDB();
      } else if (
        (msg.includes("indexeddbtransactionerror") || msg.includes("unknownerror")) &&
        msg.includes("connection to indexed database server lost")
      ) {
        console.error("IndexedDB connection error detected:", errorMsg, "at", url, "line:", lineNumber);
        this.clearIndexDB(); // Call clearIndexDB for this error too
      } else {
        console.error("Global error handler caught an error:", errorMsg, "at", url, "line:", lineNumber);
      }
      return true;
    };
  }

  private async clearIndexDB() {
    const indexedDB = window.indexedDB;
    const allDBIndex = (await indexedDB.databases()).map((x) => x?.name);

    // List of app-specific Firebase IndexedDB names that need clearing
    const firebaseDBPrefixes = ['firestore', 'firebase-messaging', 'firebase-installations'];

    allDBIndex.forEach((indexName) => {
      // Only clear databases that belong to our Firebase instance
      if (indexName && firebaseDBPrefixes.some(prefix => indexName.startsWith(prefix))) {
        const deleteRequest = indexedDB.deleteDatabase(indexName);
        deleteRequest.onsuccess = () => {
          console.info(`IndexedDB "${indexName}" cleared successfully`);
        };
        deleteRequest.onerror = (event) => {
          console.error(`Error clearing IndexedDB "${indexName}":`, event);
        };
        deleteRequest.onblocked = (event) => {
          console.warn(`Clearing IndexedDB "${indexName}" was blocked:`, event);
          // Notify the user that they might need to close other tabs
        };
      }
    });

    // After clearing, attempt to reconnect to Firebase
    try {
      const firestoreToTerminate = this.firebaseFactoryService.getFirestoreInstance();
      const appToDelete = this.firebaseFactoryService.getFirebaseApp();

      if (firestoreToTerminate) {
        await terminate(firestoreToTerminate);
      }
      if (appToDelete) {
        await deleteApp(appToDelete);
      }

      FirebaseFactoryService.resetInstances(); // Reset cached instances
      // Re-initialize by calling the getters, which will create new instances
      this.firebaseFactoryService.getFirebaseApp();
      this.firebaseFactoryService.getFirestoreInstance();
    } catch (error) {
      console.error("Failed to reconnect to Firebase after clearing IndexedDB:", error);
    }
  }

  ionViewWillLeave(): void {
    this.cleanupNetworkListeners();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  async initialize(): Promise<void> {
    await this.checkSwSchemaVersion();
  }
  private async checkSwSchemaVersion(): Promise<void> {
    try {
      const doc = await this.firestore
        .collection('pwa_version')
        .doc('current')
        .ref.get();

      if (doc.exists) {
        const data = doc.data() as { version: string };
        if (data.version !== environment.swSchemaVersion) {
          console.warn(
            `SW schema mismatch: build=${environment.swSchemaVersion}, remote=${data.version}`
          );
          await this.swForceUpdateService.attemptForceUpdate();
        }
      }
    } catch (err) {
      console.warn('SwForceUpdate: proactive version check failed', err);
    }
  }
}
